import {
  Alert,
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
} from "@mui/material";
import { useState, useEffect } from "react";
import emailjs from 'emailjs-com';
import WorkIcon from '@mui/icons-material/Work';
import UploadFileIcon from '@mui/icons-material/UploadFile';

function Careers() {
  useEffect(() => {
    document.title = "Careers | BNB Cleaning";
  }, []);
  const [formData, setFormData] = useState({
    position: "Cleaner",
    fullName: "",
    email: "",
    phone: "",
    address: "",
    totalExperience: "",
    airbnbExperience: "",
    availability: "",
    message: "",
  });

  const [resume, setResume] = useState<File | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [status, setStatus] = useState<"success" | "error" | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setResume(e.target.files[0]);
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const fullMessage = `
      Position: ${formData.position}
      Full Name: ${formData.fullName}
      Email: ${formData.email}
      Phone: ${formData.phone}
      Address: ${formData.address}
      Total Cleaning Experience: ${formData.totalExperience}
      Airbnb Cleaning Experience: ${formData.airbnbExperience}
      Availability: ${formData.availability}
      Message: ${formData.message}
      Resume: ${resume ? resume.name : 'Not provided'}
    `.trim();

    const templateParams = {
      name: formData.fullName,
      email: formData.email,
      message: fullMessage,
    };

    emailjs
      .send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID!,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID!,
        templateParams,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY!
      )
      .then(() => {
        setStatus("success");
        setSubmitted(true);
        setFormData({
          position: "Cleaner",
          fullName: "",
          email: "",
          phone: "",
          address: "",
          totalExperience: "",
          airbnbExperience: "",
          availability: "",
          message: "",
        });
        setResume(null);

        setTimeout(() => {
          setStatus(null);
        }, 6000);

        setTimeout(() => {
          setSubmitted(false);
        }, 2000);
      })
      .catch(() => {
        setStatus("error");
        setSubmitted(true);

        setTimeout(() => {
          setSubmitted(false);
          setStatus(null);
        }, 5000);
      });
  };

  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Box textAlign="center" mb={4}>
        <WorkIcon sx={{ fontSize: 60, color: "primary.main", mb: 2 }} />
        <Typography variant="h3" fontWeight="bold" gutterBottom>
          Join Our Team
        </Typography>
        <Typography variant="h6" color="text.secondary" paragraph>
          We're always looking for dedicated cleaners to join our team!
          Fill out the form below.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', mt: 2 }}>
          * Applicants must be legally authorized to work in Canada
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          * Thank you for your interest. Due to the high number of applications, only selected candidates will be contacted.
        </Typography>
      </Box>

      <Paper elevation={3} sx={{ p: 4 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Position Applying For"
                name="position"
                value={formData.position}
                onChange={handleChange}
                variant="outlined"
                placeholder="e.g., Cleaner, Housekeeper"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Full Name"
                name="fullName"
                value={formData.fullName}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="email"
                label="Email Address"
                name="email"
                value={formData.email}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                variant="outlined"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Total Years of Cleaning Experience"
                name="totalExperience"
                value={formData.totalExperience}
                onChange={handleChange}
                variant="outlined"
                placeholder="e.g., 5 years"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Years of Airbnb Cleaning Experience"
                name="airbnbExperience"
                value={formData.airbnbExperience}
                onChange={handleChange}
                variant="outlined"
                placeholder="e.g., 2 years"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Availability"
                name="availability"
                value={formData.availability}
                onChange={handleChange}
                variant="outlined"
                placeholder="e.g., Weekdays, Weekends, Full-time"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Tell us about yourself"
                name="message"
                value={formData.message}
                onChange={handleChange}
                variant="outlined"
                placeholder="Why do you want to join our team? What makes you a great cleaner?"
              />
            </Grid>

            {/* <Grid item xs={12}>
              <Box
                sx={{
                  border: "2px dashed",
                  borderColor: "divider",
                  borderRadius: 2,
                  p: 3,
                  textAlign: "center",
                }}
              >
                <UploadFileIcon sx={{ fontSize: 40, color: "text.secondary", mb: 1 }} />
                <Typography variant="body1" gutterBottom>
                  Upload Your Resume (Optional)
                </Typography>
                <Button
                  variant="outlined"
                  component="label"
                  sx={{ mt: 1 }}
                >
                  Choose File
                  <input
                    type="file"
                    hidden
                    accept=".pdf,.doc,.docx,.txt,.rtf"
                    onChange={handleFileChange}
                  />
                </Button>
                {resume && (
                  <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
                    Selected: {resume.name}
                  </Typography>
                )}
              </Box>
            </Grid> */}
          </Grid>

          {status && (
            <Alert
              severity={status}
              sx={{ mt: 2 }}
            >
              {status === "success"
                ? "Thank you for your application! We'll review it and get back to you soon."
                : "Something went wrong. Please try again or contact us directly."}
            </Alert>
          )}

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={submitted}
            sx={{ mt: 2, py: 1.5 }}
          >
            Submit Application
          </Button>
        </form>
      </Paper>
    </Container>
  );
}

export default Careers;
